import BaseNavigator from './base-navigator';
import { onboardingCompleted } from '../store/ducks/user';

const pages = {
  INTRO: 'intro',
  OTHER_PRODUCT_SELECTION: 'other-product-selection',
  CONNECT_SELECTION: 'connect-selection',
  CARDIO_APP_LINK: 'cardio-app-link',
  FUSION_APP_LINK: 'fusion-app-link',
  SLEEP_APP_LINK: 'sleep-app-link',
  ALTRA_IQ_APP_LINK: 'altra-ip-app-link',
  WEARABLE_SELECTION: 'wearable-selection',
  CREATE_ACCOUNT_LOGIN_INFO: 'create-account-login-info',
  LOGIN_WITH_EMAIL: 'login-email',
  FORGOT_PASSWORD: 'forgot-password',
  PLAN_SELECTION: 'plan-selection',
  SHIPPING_ADDRESS: 'shipping-address',
  ACTIVATION_CODE: 'activation-code',
  CHANGE_FORMULA: 'change-formula',
  GENDER: 'gender',
  CURRENT_HEIGHT: 'current-height',
  CURRENT_WEIGHT: 'current-weight',
  GOAL_WEIGHT: 'goal-weight',
  DAILY_ACTIVITY: 'daily-activity',
  WEEKLY_WEIGHT_GOAL: 'weekly-weight-goal',
  FITNESS_INTRO: 'fitness-intro',
  WORKOUT_INTENSITY: 'workout-intensity',
  FITNESS_GOAL: 'fitness-goal',
  PREFERRED_WORKOUT_DAYS: 'preferred-workout-days',
  PREFERRED_WORKOUT_LENGTH: 'preferred-workout-length',
  EQUIPMENT_PREFERENCES: 'equipment-preferences',
  CONFIRM_YOUR_FORMULA: 'confirm-your-formula',
};

export default class PhoenixNavigator extends BaseNavigator {
  static getPages() {
    return pages;
  }

  static getPageList() {
    return Object.keys(pages).map(page => pages[page]);
  }

  constructor(initPage = pages.INTRO) {
    super();

    this.initStateMachine(initPage, [
      { name: 'next', from: pages.INTRO, to: pages.CONNECT_SELECTION },
      { name: 'next', from: pages.CONNECT_SELECTION, to: this.fromConnectSelection },
      { name: 'next', from: pages.OTHER_PRODUCT_SELECTION, to: this.fromOtherProduct },
      { name: 'next', from: pages.WEARABLE_SELECTION, to: pages.CREATE_ACCOUNT_LOGIN_INFO },
      { name: 'next', from: pages.CREATE_ACCOUNT_LOGIN_INFO, to: pages.GENDER },
      { name: 'next', from: pages.LOGIN_WITH_EMAIL, to: this.fromLogin },
      { name: 'next', from: pages.GENDER, to: pages.CURRENT_HEIGHT },
      { name: 'next', from: pages.CURRENT_HEIGHT, to: pages.CURRENT_WEIGHT },
      { name: 'next', from: pages.CURRENT_WEIGHT, to: pages.GOAL_WEIGHT },
      { name: 'next', from: pages.GOAL_WEIGHT, to: this.goalWeightFork },
      { name: 'next', from: pages.DAILY_ACTIVITY, to: pages.WEEKLY_WEIGHT_GOAL },
      { name: 'next', from: pages.WEEKLY_WEIGHT_GOAL, to: this.weeklyWeightGoalFork },
      { name: 'next', from: [pages.ACTIVATION_CODE, pages.SHIPPING_ADDRESS, pages.PLAN_SELECTION], to: pages.FITNESS_INTRO },
      { name: 'next', from: pages.FITNESS_INTRO, to: pages.WORKOUT_INTENSITY },
      { name: 'next', from: pages.WORKOUT_INTENSITY, to: pages.FITNESS_GOAL },
      { name: 'next', from: pages.FITNESS_GOAL, to: pages.PREFERRED_WORKOUT_LENGTH },
      { name: 'next', from: pages.PREFERRED_WORKOUT_LENGTH, to: pages.PREFERRED_WORKOUT_DAYS },
      { name: 'next', from: pages.PREFERRED_WORKOUT_DAYS, to: pages.EQUIPMENT_PREFERENCES },
      { name: 'next', from: pages.EQUIPMENT_PREFERENCES, to: pages.CONFIRM_YOUR_FORMULA },
      { name: 'previous', from: pages.CURRENT_HEIGHT, to: pages.GENDER },
      { name: 'previous', from: pages.CURRENT_WEIGHT, to: pages.CURRENT_HEIGHT },
      { name: 'previous', from: pages.GOAL_WEIGHT, to: pages.CURRENT_WEIGHT },
      { name: 'previous', from: pages.DAILY_ACTIVITY, to: pages.GOAL_WEIGHT },
      { name: 'previous', from: pages.WEEKLY_WEIGHT_GOAL, to: this.fromWeeklyWeightGoal },
      { name: 'previous', from: pages.WORKOUT_INTENSITY, to: pages.FITNESS_INTRO },
      { name: 'previous', from: pages.FITNESS_GOAL, to: pages.WORKOUT_INTENSITY },
      { name: 'previous', from: pages.PREFERRED_WORKOUT_LENGTH, to: pages.FITNESS_GOAL },
      { name: 'previous', from: pages.PREFERRED_WORKOUT_DAYS, to: pages.PREFERRED_WORKOUT_LENGTH },
      { name: 'previous', from: pages.EQUIPMENT_PREFERENCES, to: pages.PREFERRED_WORKOUT_DAYS }
    ], 'phoenix');
  }

  jumpTo(pageName) {
    const realPage = {
      'login': pages.LOGIN_WITH_EMAIL,
      'forgot-password': pages.FORGOT_PASSWORD,
      'join': pages.CREATE_ACCOUNT_LOGIN_INFO,
      'shipping-address': pages.SHIPPING_ADDRESS,
      'activation-code': pages.ACTIVATION_CODE,
      'current-weight': pages.CURRENT_WEIGHT,
      'create-account-login-info': pages.CREATE_ACCOUNT_LOGIN_INFO
    }[pageName];

    if (!realPage) throw new Error(`Invalid Navigator JumpTo destination: ${pageName}`);

    return super.jumpTo(realPage);
  }

  fromConnectSelection(appState) {
    if (appState.connectedProduct === 'other') return pages.OTHER_PRODUCT_SELECTION;

    return pages.WEARABLE_SELECTION;
  }

  fromOtherProduct(appState) {
    return {
      'cardio': pages.CARDIO_APP_LINK,
      'fusion': pages.FUSION_APP_LINK,
      'sleep': pages.SLEEP_APP_LINK,
      'altra-iq': pages.ALTRA_IQ_APP_LINK
    }[appState.connectedProduct];
  }

  goalWeightFork(state) {
    return state.navigationFlow.flowType === 'confirmFormula' ? pages.WEEKLY_WEIGHT_GOAL : pages.DAILY_ACTIVITY;
  }

  weeklyWeightGoalFork(state) {
    if (state.navigationFlow.flowType === 'confirmFormula') return pages.CONFIRM_YOUR_FORMULA;

    return state.user.premium ? pages.FITNESS_INTRO : pages.PLAN_SELECTION;
  }

  fromLogin(state, dispatch) {
    function finishOnboarding() {
      return pages.GENDER;
    }

    if (!Boolean(state.user.gender)) return finishOnboarding();
    if (!Boolean(state.user.height)) return finishOnboarding();
    if (!Boolean(state.user.weight)) return finishOnboarding();
    if (!Boolean(state.user.goal_weight)) return finishOnboarding();
    // goal weight is only relevant if the weight and goal weight do not match
    if (state.user.weight && state.user.goal_weight && state.user.weight !== state.user.goal_weight && !Boolean(state.user.target_weight_change)) {
      return finishOnboarding();
    }
    if (!Boolean(state.user.activity_level)) return finishOnboarding();

    if (state.user.premium) {
      if (!Boolean(state.user.fitness_goal)) return finishOnboarding();
      if (!Boolean(state.user.preferred_workout_length_min) && !Boolean(state.user.preferred_workout_length_max)) return finishOnboarding();
      if (!Boolean(state.user.workout_days && state.user.workout_days.length)) return finishOnboarding();
      // A user can have no equipment, which is an empty array and is the default, so we don't need to check equipment
    }

    dispatch(onboardingCompleted(state.connectedProduct));
    return pages.LOGIN_WITH_EMAIL;
  }

  fromWeeklyWeightGoal(state) {
    return state.navigationFlow.flowType === 'confirmFormula' ? pages.GOAL_WEIGHT : pages.DAILY_ACTIVITY;
  }
}
